<template>
  <section class="bg-[#F5F1ED]">
    <div class="px-4 py-8 mx-auto max-w-screen-2xl md:py-16 lg:px-6 sm:px-7">
      <div class="p-10 bg-white">
        <div class="mx-auto md:pb-6">
            <h2
                class="mx-auto font-bold text-center md:px-2 xl:px-20 md:text-4xl font-ww-bold text-balance xl:!leading-none xl:text-42 text-3xl"
                v-html="headingText">
            </h2>

            <div class="flex flex-col items-center justify-center py-8 md:mt-12 gap-x-8 md:flex-row gap-y-2 sm:gap-y-8">
                <div class="flex items-center justify-center gap-x-3 gap-y-4 md:mr-8">
                    <div class="text-5xl xl:text-7xl md:text-6xl font-ww-regular text-lifeboost-red">
                        {{ ratings?.avg_rating }}
                    </div>
                    <div class="flex flex-col items-start justify-between h-full">
                        <div class="mb-2 md:mb-2">
                            <SectionRatingStarsRating :rating="5" />
                        </div>
                        <div class="text-xl leading-7 xl:text-2xl font-ww-regular">
                            Based on {{ ratings?.reviews_count }} reviews
                        </div>
                    </div>
                </div>
                <div class="relative flex h-full py-2.5">
                    <div class="absolute w-0.5 md:-left-8 md:block hidden h-full bottom-0 bg-[#cdcdcd]"></div>
                    <a href="https://lifeboostcoffee.com/products/single-origin-medium-roast-decaf-coffee#judgeme_product_reviews" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="px-10 py-3 text-lg text-white rounded-full md:py-4 bg-lifeboost-red md:text-xl xl:text-2xl font-ww-regular"
                        aria-label="Write a review for Lifeboost Cognition Coffee"
                        title="Write a review for Lifeboost Cognition Coffee">
                        Write a review
                    </a>
                </div>
            </div>
        </div>

        <!-- Reviews List -->
        <div class="mx-auto" ref="reviewsSection">
            <div v-for="(review, index) in reviews" :key="review.id" role="article" tabindex="0"
                :aria-labelledby="'review-' + review.id"
                class="w-full py-6 overflow-hidden border-b-2 md:py-14 bg-lifeboost-blue-lighter border-gray"
                :class="{ 'border-none': index === reviews.length - 1 }">
                <!-- Review Content -->
                <div class="grid grid-cols-12 gap-x-6 md:gap-y-0 gap-y-4">
                    <!-- Reviewer Info -->
                    <div class="flex items-center col-span-12 mb-6 lg:col-span-4 xl:col-span-3 gap-x-4 xl:mb-0">
                        <div>
                            <div
                                class="flex items-center justify-center w-20 h-20 text-white rounded-full lg:h-24 lg:w-24 bg-lifeboost-gray">
                                <div class="flex items-center justify-center leading-none text-53">
                                    {{ review.reviewer.name[0] }}
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col w-full gap-y-3 md:w-auto">
                            <div role="heading" aria-level="2" :id="'review-' + review.id"
                                class="flex flex-wrap items-center justify-between font-heading gap-x-4 sm:flex-row">
                                <div
                                    class="text-lg font-bold md:text-xl xl:text-2xl font-ww-medium">
                                    {{ review.reviewer.name }}
                                </div>
                                <div class="text-xl md:hidden">
                                    {{ formatDate(review.created_at) }}
                                </div>
                            </div>
                            <div>
                                <span
      class="inline-flex items-center font-ww-regular font-normal px-2 py-1.5 rounded-full bg-lifeboost-green-dark text-lifeboost-green-dark-green">
     
      <span class="mx-2 text-sm text-white md:text-lg">Verified Buyer</span>
      <!-- <span class="sr-only">{{ component.srText }}</span> -->
    </span>
                            </div>
                        </div>
                    </div>

                    <!-- Review Details -->
                    <div class="grid grid-cols-12 col-span-12 mt-6 lg:col-span-8 xl:col-span-9 md:gap-x-8 md:mt-0">
                        <div class="flex flex-col justify-center col-span-12 lg:pr-10">
                            <div class="flex items-center justify-between md-max:w-full">
                                <div class="flex items-center gap-x-6 md-max:w-full md-max:flex md-max:gap-y-3 md-max:flex-wrap md-max:justify-start">
                                    <div class="float-left md-max:pr-2">
                                        <SectionRatingStarsRating :rating="review.rating" />
                                    </div>
                                    <div class="font-bold font-ww-medium text-xl md:text-2xl md-max:-mt-0.5"
                                        v-if="review.title">
                                        {{ review.title }}
                                    </div>
                                </div>
                                <div class="hidden text-lg md:block font-ww-regular md:text-xl xl:text-2xl">
                                    {{ formatDate(review.created_at) }}
                                </div>
                            </div>
                            <div class="pt-4">
                                <div class="flex flex-wrap lg:w-10/12">
                                    <div class="w-full">
                                        <div
                                            class="max-w-2xl text-lg md:text-lg xl:text-xl font-ww-regular">
                                            {{ review.body }}
                                        </div>
                                        <!-- <div v-if="review.pictures && review.pictures.some((el) => !el.hidden)"
                                            class="flex flex-row max-w-2xl mb-8 leading-loose">
                                            <ElementImageReviews :center="false" :pictures="review.pictures"
                                                @on-show="(index, array) => $emit('onShow', index, array)" />
                                        </div> -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- End of Review Content -->
            </div>

            <div class="flex justify-center mt-6 space-x-4">
                <!-- Previous Button -->
                <button aria-label="Previous review" @click="changePage(currentPage - 1)" v-if="currentPage > 1 && !loading" class="pr-2">
                    <svg class="w-4 rotate-180 sm:w-auto" xmlns="http://www.w3.org/2000/svg" width="14px" height="24px" viewBox="0 0 14 24">
                        <path fill-rule="evenodd" fill="rgb(0, 0, 0)"
                            d="M13.686,11.511 C13.779,11.975 13.647,12.476 13.287,12.836 L3.457,22.665 C2.886,23.237 1.960,23.237 1.389,22.666 C0.819,22.096 0.819,21.170 1.391,20.598 L10.260,11.730 L1.384,2.854 C0.814,2.283 0.814,1.358 1.386,0.786 C1.957,0.215 2.883,0.214 3.454,0.785 L13.270,10.601 C13.524,10.855 13.658,11.179 13.686,11.511 Z" />
                    </svg>
                </button>

                <!-- Page Numbers -->
                <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="changePage(page)"
                    class="text-2xl 2xl:text-3xl"
                    :class="['cursor-pointer ', { 'font-bold text-34 mb-1 md:pt-0': currentPage === page, 'text-gray-400 !font-medium': currentPage !== page }]"
                >
                    {{ page }}
                </button>

                <!-- Next Button -->
                <button aria-label="Next review" @click="changePage(currentPage + 1)" v-if="currentPage < totalPages && !loading" class="pl-2 text-27">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 sm:w-auto" width="14px" height="24px" viewBox="0 0 14 24">
                        <path fill-rule="evenodd" fill="rgb(0, 0, 0)"
                            d="M13.686,11.511 C13.779,11.975 13.647,12.476 13.287,12.836 L3.457,22.665 C2.886,23.237 1.960,23.237 1.389,22.666 C0.819,22.096 0.819,21.170 1.391,20.598 L10.260,11.730 L1.384,2.854 C0.814,2.283 0.814,1.358 1.386,0.786 C1.957,0.215 2.883,0.214 3.454,0.785 L13.270,10.601 C13.524,10.855 13.658,11.179 13.686,11.511 Z" />
                    </svg>
                </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center mt-4">
                <span>Loading reviews...</span>
            </div>

            <!-- Error State -->
            <div v-if="error" class="flex justify-center mt-4 text-red-500">
                <span>{{ error }}</span>
            </div>
        </div>
      </div>

      <div v-if="showButton" class="my-6 md:my-10">
            <button @click="scrollTo(buttonHref)"
            class="flex items-center px-12 py-3 mx-auto text-base rounded-full md:text-xl md:px-24 lg:py-4 font-ww-medium btn-red gap-x-2">{{ buttonText }}</button>
          </div>
    </div>

  


  </section>
</template>

<script setup>

import { ref, computed, onMounted } from 'vue';

const props = defineProps({
    headingText: {
        type: String,
        default: "Hear from People Loving the New Lifeboost Cognition Coffee, Upgraded with 100% Real Mushrooms"
    },
    productId: {
        type: String,
        default: "7387365703740"
    },
    buttonText: {
        type: String,
        default: "Get Discount"
    },
    buttonHref: {
        type: String,
        default: "products"
    },
    showButton: {
        type: Boolean,
        default: true
    }
});

const reviews = ref([]);
const currentPage = ref(1);
const reviewsPerPage = ref(6);
const totalPages = ref(1);
const loading = ref(false);
const error = ref(null);
const hasMoreReviews = ref(true);
const ratings = ref();

const reviewsSection = ref(null);

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0'); 
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

const fetchReviews = async (productId, page = 1) => {
    loading.value = true;
    error.value = null;

    try {
        const response = await fetch(
            `/api/fetchReviews?product_id=${productId}&page=${page}&limit=${reviewsPerPage.value}`
        );

        fetchRaitingReview(productId, 'cognition-mushroom-coffee');

        if (!response.ok) {
            throw new Error('Failed to fetch reviews');
        }

        const data = await response.json();
        if (Array.isArray(data.reviews)) {
            reviews.value = data.reviews;
            totalPages.value = Math.ceil((ratings.value?.reviews_count || 0) / reviewsPerPage.value);
            hasMoreReviews.value = data.reviews.length === reviewsPerPage.value;
        } else {
            reviews.value = [];
            hasMoreReviews.value = false;
        }
    } catch (err) {
        error.value = err.message || 'An error occurred while fetching reviews.';
    } finally {
        loading.value = false;
    }
};

const fetchRaitingReview = async (productId, handle) => {
    try {
        const response = await fetch(
            `/api/fetchReviewsCount?product_id=${productId}&handle=${handle}`
        );

        if (!response.ok) {
            throw new Error('Failed to fetch reviews');
        }

        const data = await response.json();
        ratings.value = data;
    } catch (err) {
        error.value = err.message || 'An error occurred while fetching reviews.';
    } finally {
        loading.value = false;
    }
};

const changePage = (page) => {
    if (page < 1 || page > totalPages.value) return;
    currentPage.value = page;
    fetchReviews(props.productId, currentPage.value);

    if (reviewsSection.value) {
        reviewsSection.value.scrollIntoView({ behavior: 'smooth' });
    }
};

const visiblePages = computed(() => {
    let start = Math.max(1, currentPage.value - 1);
    let end = Math.min(totalPages.value, currentPage.value + 2);

    if (end - start < 3 && start > 1) {
        start = Math.max(1, end - 3);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

onMounted(() => {
    fetchRaitingReview(props.productId, 'cognition-mushroom-coffee').then(() => {
        fetchReviews(props.productId, currentPage.value);
    });
});

defineBuilderComponent({
    friendlyName: "Review Product",
    inputs: [
        {
            name: "headingText",
            type: "richText",
            friendlyName: "Heading Text",
            defaultValue: "Hear from People Loving the New Lifeboost Cognition Coffee, Upgraded with 100% Real Mushrooms",
        },
        {
            name: "productId",
            type: "string",
            friendlyName: "Product ID",
            defaultValue: "7387365703740",
        },
        {
            name: "buttonText",
            type: "text",
            friendlyName: "Button Text",
            defaultValue: "Get Discount",
        },
        {
            name: "buttonHref",
            type: "text",
            friendlyName: "Button Link",
            defaultValue: "products",
        },
        {
            name: "showButton",
            type: "boolean",
            friendlyName: "Show Button",
            defaultValue: true,
        }
    ]
});
</script>
